export type TreeItem = { id: string; parent_id?: string | null; sort_order: string };
export type TreeNode<T> = { item: T; depth: number; children: TreeNode<T>[] };
export type DropPosition = "before" | "inside" | "after";
export type TreeMove = { parent_id: string | null; before_id?: string; after_id?: string };

export function compareTreeOrder(a: TreeItem, b: TreeItem): number {
  return a.sort_order < b.sort_order ? -1 : a.sort_order > b.sort_order ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Missing/filtered parents become visible roots. Legacy cycles never hide rows. */
export function entityTree<T extends TreeItem>(items: T[], compare: (a: T, b: T) => number = compareTreeOrder): TreeNode<T>[] {
  const sorted = [...items].sort(compare);
  const ids = new Set(sorted.map((item) => item.id));
  const children = new Map<string, T[]>();
  for (const item of sorted) if (item.parent_id && ids.has(item.parent_id)) {
    const siblings = children.get(item.parent_id) ?? [];
    siblings.push(item);
    children.set(item.parent_id, siblings);
  }
  const seen = new Set<string>();
  const build = (item: T, depth: number): TreeNode<T> => {
    seen.add(item.id);
    return { item, depth, children: (children.get(item.id) ?? []).filter((child) => !seen.has(child.id)).map((child) => build(child, depth + 1)) };
  };
  const roots = sorted.filter((item) => !item.parent_id || !ids.has(item.parent_id)).map((item) => build(item, 0));
  for (const item of sorted) if (!seen.has(item.id)) roots.push(build(item, 0));
  return roots;
}

export function flattenEntityTree<T extends TreeItem>(nodes: TreeNode<T>[], collapsed: Set<string>): TreeNode<T>[] {
  return nodes.flatMap((node) => [node, ...(collapsed.has(node.item.id) ? [] : flattenEntityTree(node.children, collapsed))]);
}

/** Return one atomic parent/order patch. Descendants retain their own parent IDs. */
export function entityMove(items: TreeItem[], sourceId: string, targetId: string | null, position: DropPosition): TreeMove | null {
  const byId = new Map(items.map((item) => [item.id, item]));
  if (!byId.has(sourceId) || sourceId === targetId) return null;
  const target = targetId ? byId.get(targetId) : undefined;
  if (targetId && !target) return null;
  const parent = target ? position === "inside" ? target.id : target.parent_id ?? null : null;
  const seen = new Set([sourceId]);
  let current = parent;
  while (current) {
    if (seen.has(current)) return null;
    seen.add(current);
    current = byId.get(current)?.parent_id ?? null;
  }
  if (target && position !== "inside") return { parent_id: parent, [position === "before" ? "before_id" : "after_id"]: target.id };
  const siblings = items.filter((item) => item.id !== sourceId && (item.parent_id ?? null) === parent).sort(compareTreeOrder);
  return { parent_id: parent, ...(siblings.length ? { after_id: siblings[siblings.length - 1]!.id } : {}) };
}

export function dropPosition(y: number, top: number, height: number): DropPosition {
  const fraction = (y - top) / height;
  return fraction < 0.25 ? "before" : fraction > 0.75 ? "after" : "inside";
}
